import { debounce } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  selectIsSaving,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import type { ConceptRef, DomainFilter, NumericRange } from '@verantos-dev/types-cohorts';

import { useConceptGroupMaps } from '../../hooks/useConceptGroupMaps';
import {
  extractConceptNames,
  extractDomainFilterFields,
  findFilterById,
  getConceptRefKeys,
  resolveNamesToConceptRef,
} from '../../utils/analyticalVariable.utils';
import { useAnalyticalVariableCardContext } from '../AnalyticalVariableCard/AnalyticalVariableCardContext';

export function useDomainFilterCard(filter: DomainFilter) {
  const intl = useIntl();
  const {
    analyticalVariable,
    conceptGroups,
    conceptGroupsByDomain,
    isEditable,
    onAnalyticalVariableChange,
  } = useAnalyticalVariableCardContext();
  // TODO: cleanup with explicit_save ff
  const isExplicitSave = useFeatureFlagSplit('explicit_save');
  const rawIsSaving = useCohortDefinitionStore(selectIsSaving);
  const isSaving = isExplicitSave ? false : rawIsSaving;

  const unavailableTooltip = intl.formatMessage({
    id: 'projects.analyticalVariables.logic.conceptGroupUnavailable',
  });
  const savingTooltip = intl.formatMessage({
    id: 'projects.analyticalVariables.logic.savingTooltip',
  });

  const {
    idToNameMap: conceptGroupIdToNameMap,
    nameToIdMap: conceptGroupNameToIdMap,
    nameToOwnerMap: conceptGroupNameToOwnerMap,
  } = useConceptGroupMaps(conceptGroups);

  const { conceptRefRows, numericFields, correlatedFilters } = useMemo(
    () => extractDomainFilterFields(intl, filter),
    [intl, filter]
  );

  // --- Concept group row edit state ---
  const [editedNamesMap, setEditedNamesMap] = useState<Map<number, string[]>>(new Map());
  // Ref mirrors editedNamesMap so flushPendingChanges always reads the latest value
  const editedNamesMapRef = useRef(editedNamesMap);
  editedNamesMapRef.current = editedNamesMap;
  // Track rows with un-flushed changes (local-only, not yet propagated to auto-save)
  const pendingRowsRef = useRef<Set<number>>(new Set());
  // When set, auto-opens the add dropdown on this row (triggered by trying to remove the last CG)
  const [autoOpenRowIdx, setAutoOpenRowIdx] = useState<number | null>(null);

  const getEditedNames = useCallback(
    (rowIdx: number): string[] | undefined => editedNamesMap.get(rowIdx),
    [editedNamesMap]
  );

  const resolveNames = useCallback(
    (rowIdx: number, conceptRef: ConceptRef | null): string[] =>
      editedNamesMap.get(rowIdx) ?? extractConceptNames(conceptRef, conceptGroupIdToNameMap),
    [editedNamesMap, conceptGroupIdToNameMap]
  );

  const propagateLogicChange = useCallback(
    (rowIdx: number, updatedNames: string[]) => {
      if (!onAnalyticalVariableChange || !analyticalVariable.logic) return;

      const filterId = (filter as unknown as Record<string, unknown>).id as string | undefined;
      if (!filterId) return;

      const logicClone = structuredClone(analyticalVariable.logic);
      const target = findFilterById(logicClone as Record<string, unknown>, filterId);
      if (!target) return;

      const refKeys = getConceptRefKeys(target);
      const key = refKeys[rowIdx];
      if (!key || !(key in target)) return;

      target[key] = resolveNamesToConceptRef(
        (target[key] as object) ?? {},
        updatedNames,
        conceptGroupNameToIdMap
      );
      onAnalyticalVariableChange({ logic: logicClone });
    },
    [analyticalVariable.logic, filter, onAnalyticalVariableChange, conceptGroupNameToIdMap]
  );

  /** Update local state only — defers save until flushPendingChanges is called. */
  const addName = useCallback(
    (rowIdx: number, conceptRef: ConceptRef | null, name: string) => {
      const current = resolveNames(rowIdx, conceptRef);
      const updatedNames = [...current, name];
      setEditedNamesMap((prev) => {
        const next = new Map(prev);
        next.set(rowIdx, updatedNames);
        return next;
      });
      pendingRowsRef.current.add(rowIdx);
    },
    [resolveNames]
  );

  // Stable ref so the debounced function always calls the latest propagateLogicChange
  const propagateRef = useRef(propagateLogicChange);
  propagateRef.current = propagateLogicChange;

  const debouncedPropagate = useMemo(
    () =>
      debounce((rowIdx: number, updatedNames: string[]) => {
        propagateRef.current(rowIdx, updatedNames);
      }, 300),
    []
  );

  useEffect(() => () => debouncedPropagate.clear(), [debouncedPropagate]);

  const removeName = useCallback(
    (rowIdx: number, conceptRef: ConceptRef | null, name: string) => {
      const current = resolveNames(rowIdx, conceptRef);
      const updatedNames = current.filter((n) => n.toLowerCase() !== name.toLowerCase());

      setEditedNamesMap((prev) => {
        const next = new Map(prev);
        next.set(rowIdx, updatedNames);
        return next;
      });

      if (updatedNames.length === 0) {
        // Last CG removed: show empty row, open dropdown, defer emit until dropdown closes
        setAutoOpenRowIdx(rowIdx);
        pendingRowsRef.current.add(rowIdx);
      } else {
        debouncedPropagate(rowIdx, updatedNames);
      }
    },
    [resolveNames, debouncedPropagate]
  );

  /** Flush all pending add-name changes to trigger auto-save. Called when the dropdown closes. */
  const flushPendingChanges = useCallback(
    (rowIdx: number, conceptRef: ConceptRef | null) => {
      const wasAutoOpen = autoOpenRowIdx === rowIdx;
      setAutoOpenRowIdx(null);

      if (!pendingRowsRef.current.has(rowIdx)) return;
      pendingRowsRef.current.delete(rowIdx);

      const names =
        editedNamesMapRef.current.get(rowIdx) ??
        extractConceptNames(conceptRef, conceptGroupIdToNameMap);

      if (wasAutoOpen && names.length === 0) {
        // User dismissed without adding — restore the original names, don't emit
        setEditedNamesMap((prev) => {
          const next = new Map(prev);
          next.delete(rowIdx);
          return next;
        });
        return;
      }

      propagateLogicChange(rowIdx, names);
    },
    [autoOpenRowIdx, conceptGroupIdToNameMap, propagateLogicChange]
  );

  const updateNumericField = useCallback(
    (fieldName: string, updated: NumericRange) => {
      if (!onAnalyticalVariableChange || !analyticalVariable.logic) return;

      const filterId = (filter as unknown as Record<string, unknown>).id as string | undefined;
      if (!filterId) return;

      const logicClone = structuredClone(analyticalVariable.logic);
      const target = findFilterById(logicClone as Record<string, unknown>, filterId);
      if (!target) return;

      target[fieldName] = updated;
      onAnalyticalVariableChange({ logic: logicClone });
    },
    [analyticalVariable.logic, filter, onAnalyticalVariableChange]
  );

  const isConceptGroupsLoading = !conceptGroups?.length;

  return {
    intl,
    conceptGroups,
    conceptGroupsByDomain,
    isEditable,
    unavailableTooltip,
    conceptGroupIdToNameMap,
    conceptGroupNameToOwnerMap,
    conceptRefRows,
    numericFields,
    correlatedFilters,
    getEditedNames,
    addName,
    removeName,
    flushPendingChanges,
    updateNumericField,
    autoOpenRowIdx,
    isConceptGroupsLoading,
    isSaving,
    savingTooltip,
  };
}
